/** Max upload payload — matches the 5 MB limit used across the app. */
export const UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

const MAX_DIMENSION = 2048;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to read image file"));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function drawToCanvas(img, maxWidth, maxHeight) {
  const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

async function encodeUnderLimit(canvas, maxBytes) {
  const outputType = "image/jpeg";
  let quality = 0.85;
  let blob = await canvasToBlob(canvas, outputType, quality);

  while (blob && blob.size > maxBytes && quality > 0.35) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, outputType, quality);
  }

  if (!blob || blob.size > maxBytes) {
    return null;
  }

  return { blob, outputType };
}

/**
 * Resize and compress an image so uploads stay under the server body-size limit.
 * Returns the original file when it is already small enough.
 */
export async function compressImageForUpload(file, maxBytes = UPLOAD_MAX_BYTES) {
  if (!file) return null;
  if (file.size <= maxBytes) return file;

  const img = await loadImageFromFile(file);
  let canvas = drawToCanvas(img, MAX_DIMENSION, MAX_DIMENSION);
  let encoded = await encodeUnderLimit(canvas, maxBytes);

  if (!encoded) {
    canvas = drawToCanvas(img, 1600, 1600);
    encoded = await encodeUnderLimit(canvas, maxBytes);
  }

  if (!encoded) {
    canvas = drawToCanvas(img, 1280, 1280);
    encoded = await encodeUnderLimit(canvas, maxBytes);
  }

  if (!encoded) {
    canvas = drawToCanvas(img, 1024, 1024);
    encoded = await encodeUnderLimit(canvas, maxBytes);
  }

  if (!encoded) {
    throw new Error("Image is too large. Please use a file under 5 MB.");
  }

  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([encoded.blob], `${baseName}.jpg`, {
    type: encoded.outputType,
    lastModified: Date.now(),
  });
}
